import PhotoVariant from "src/api/graphql/__generated__/enums/PhotoVariant";
import getRandomString from "src/helpers/getRandomString";
import { enumNever } from "src/shared/helpers/generalHelpers";
import { getCurTime_S } from "src/shared/helpers/timeHelpers";
import { getAssetPersist } from "../stores/useAssetPersist";
import { PersistAssetInfo, RemoteFileUploadAssetInfo, UploadAssetInfo } from "../types";
import { QueuePersistImagesOptions, QueuePersistImagesResponse } from "./types";

const parseFileUriArgs = (
	asset: RemoteFileUploadAssetInfo,
): Pick<UploadAssetInfo, "fileInfo" | "uriInfo"> => {
	const { variant, image, video } = asset;
	switch (variant) {
		case PhotoVariant.Image:
			const imageUri = URL.createObjectURL(image.file);
			return {
				fileInfo: {
					variant: PhotoVariant.Image,
					image: {
						file: image.file,
					},
				},
				uriInfo: {
					variant: PhotoVariant.Image,
					image: {
						uri: imageUri,
						release: () => URL.revokeObjectURL(imageUri),
					},
				},
			};
		case PhotoVariant.Video:
			const videoUri = URL.createObjectURL(video.file);
			return {
				fileInfo: {
					variant: PhotoVariant.Video,
					video: {
						file: video.file,
					},
				},
				uriInfo: {
					variant: PhotoVariant.Video,
					video: {
						uri: videoUri,
						release: () => URL.revokeObjectURL(videoUri),
					},
				},
			};
		case PhotoVariant.LivePhoto:
			throw new Error("Live photo not supported on web");
		default:
			return enumNever(variant);
	}
};

const queueUpload = async (
	input: PersistAssetInfo[],
	{}: QueuePersistImagesOptions = {},
): Promise<QueuePersistImagesResponse> => {
	if (input.length === 0) return { didError: false };
	if (input.some(({ type }) => type !== "file")) {
		throw new Error("Only file persist is supported on web");
	}

	const { pushUploadAssets } = getAssetPersist();
	const cur = getCurTime_S();
	pushUploadAssets(
		(input as RemoteFileUploadAssetInfo[]).map((assetInfo) => {
			return {
				isThumbnail: assetInfo.isThumbnail ?? false,
				gatheringId: assetInfo.gatheringId,
				localPhotoId: getRandomString(),
				timestamp: cur,
				stateUpdatedAt: cur,
				uploadState: "Pending",
				localSelectionId: assetInfo.localSelectionId,
				localSelectionIndex: assetInfo.localSelectionIndex,
				...parseFileUriArgs(assetInfo),
			} satisfies UploadAssetInfo;
		}),
	);
	return { didError: false };
};
export default queueUpload;
